import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { BooleanParam, JsonParam, NumberParam, useQueryParam } from 'use-query-params';

import { JobFilters } from 'api/jobs';
import { useActiveActionDialogType, useAdditionalParamsDialog } from 'components/ActionDialog';
import { useMultiplexWorkflow } from 'components/StudyDashboard/Pipelines/Multiplex/MultiplexWorkflowContext';
import { AnalysisAreasExploreGuide } from 'components/StudyDashboard/Workflow/AnalysisAreasExploreGuide';
import { ANALYSIS_AREAS_EXPLORE_PARAM } from 'components/StudyDashboard/Workflow/analysisAreasExplore';
import { WorkflowTab, useStudyDashboardTabState } from 'components/StudyDashboard/useStudyDashboardTabState';
import { getActiveAnnotationAssignmentIdViewerKey } from 'services/annotations/useAnnotationQueryParams';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';

export const DatasetWorkflowStatus: React.FC = () => {
  const { overview } = useMultiplexWorkflow();
  const [, setActiveTab] = useStudyDashboardTabState();
  const [, setCurrentActionItemType] = useActiveActionDialogType();
  const [, updateAdditionalParams] = useAdditionalParamsDialog();
  const [chipElement, setChipElement] = useState<HTMLDivElement | null>(null);
  const [analysisAreasExplore] = useQueryParam(ANALYSIS_AREAS_EXPLORE_PARAM, BooleanParam);
  const [datasetJobFilters] = useQueryParam<Partial<JobFilters> | null | undefined>('datasetJobFilters', JsonParam);
  const [assignmentId] = useQueryParam(getActiveAnnotationAssignmentIdViewerKey(0), NumberParam);
  const { getUrlToSlidePage } = useNavigationToViewerPage();
  const firstSlideId = datasetJobFilters?.slideIds?.find((slideId) => Boolean(slideId));
  const openSlideUrl =
    analysisAreasExplore && firstSlideId
      ? getUrlToSlidePage({
          slideId: firstSlideId,
          activeAnnotationAssignment: assignmentId ?? undefined,
        })
      : undefined;

  const { currentStage, recommendedActionStage, hasScope } = overview;
  const actionType = recommendedActionStage?.stage.actionType;
  // Clear rule: slides not yet organized into cases promotes the manifest step; a stage that
  // needs a human decision (approval) reads "Action required"; otherwise show the actual stage.
  const currentStepLabel = analysisAreasExplore
    ? 'Annotate analysis areas'
    : !hasScope
    ? 'Start with slides'
    : currentStage?.stage.id === 'manifest'
    ? 'Update case manifest'
    : currentStage?.stage.decisionPoint
    ? 'Action required'
    : currentStage?.stage.label || 'Workflow complete';
  const actionLabel = analysisAreasExplore
    ? 'Open slide'
    : actionType
    ? recommendedActionStage?.stage.actionLabel || `Run ${recommendedActionStage?.stage.label}`
    : currentStage?.stage.id === 'manifest'
    ? 'Update case manifest'
    : currentStage?.stage.decisionPoint
    ? 'Action required'
    : hasScope
    ? 'Review workflow'
    : 'Open workflow';

  const handleAction = () => {
    if (!actionType) {
      setActiveTab(WorkflowTab);
      return;
    }

    setCurrentActionItemType(actionType);
    if (recommendedActionStage?.stage.additionalParams) {
      updateAdditionalParams(recommendedActionStage.stage.additionalParams);
    }
  };

  return (
    <>
      <Paper
        ref={setChipElement}
        data-testid="dataset-workflow-status"
        variant="outlined"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          width: 'fit-content',
          maxWidth: '100%',
          minWidth: 0,
          minHeight: 40,
          px: 1.25,
          py: 0.5,
          borderRadius: (theme) => `${theme.shape.borderRadius}px`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, maxWidth: '100%' }}>
          <AccountTreeOutlinedIcon color="secondary" fontSize="small" />
          <Tooltip
            title={
              analysisAreasExplore
                ? 'Annotate Include and Exclude on the tissue mask'
                : currentStage?.summary || currentStepLabel
            }
            placement="top"
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: '0 1 auto' }}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                Workflow
              </Typography>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 700, minWidth: 0, maxWidth: { xs: 170, md: 220, xl: 300 } }}
              >
                {currentStepLabel}
              </Typography>
            </Stack>
          </Tooltip>
          {analysisAreasExplore ? (
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              component={openSlideUrl ? RouterLink : 'button'}
              to={openSlideUrl}
              disabled={!openSlideUrl}
              endIcon={<ArrowForwardOutlinedIcon />}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              <Typography component="span" variant="caption" noWrap>
                Open slide
              </Typography>
            </Button>
          ) : (
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              endIcon={<ArrowForwardOutlinedIcon />}
              onClick={handleAction}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              <Typography component="span" variant="caption" noWrap sx={{ maxWidth: { xs: 180, md: 140, xl: 220 } }}>
                {actionLabel}
              </Typography>
            </Button>
          )}
        </Stack>
      </Paper>
      {analysisAreasExplore ? <AnalysisAreasExploreGuide variant="popper" anchorEl={chipElement} /> : null}
    </>
  );
};
